<template>
  <div>
    <TopBar currentTitle="Recipe Collections">
      <b-button type="is-primary" size="is-small" icon-left="plus" @click="createNewRecipeCollection">Add Collection</b-button>
    </TopBar>
    <div class="page-content">
    <h1 class="page-title">Recipe Collections</h1>
    <p class="page-subtitle mb-4">{{ collections.length }} total</p>

    <!-- Filters Bar -->
    <div class="filters-bar">
      <b-select v-model="statusFilter" size="is-small" @input="onStatusFilterChange" placeholder="All statuses">
        <option :value="''">All statuses</option>
        <option value="draft">Draft</option>
        <option value="test">Test</option>
        <option value="fix">Fix</option>
        <option value="live">Live</option>
      </b-select>
    </div>

    <!-- Table Card -->
    <div class="feeds-card">
      <div class="feeds-card-body compact">
        <b-table
          :data="filteredCollections"
          :hoverable="true"
          :mobile-cards="false"
          :default-sort="[sortField, sortDirection]"
          @sort="onSort"
          @click="openRecipeCollection">

          <b-table-column width=48 label="Image" sortable v-slot="props">
            <figure class="m-0 p-0 image is-48x48">
              <img v-if="rowImageSrc(props.row) == null" class="thumbnail" src="@/assets/listing-placeholder.png">
              <img v-else class="thumbnail" :src="rowImageSrc(props.row)">
            </figure>
          </b-table-column>

          <b-table-column field="title" label="Title" sortable v-slot="props">
            <p class="has-text-weight-bold">{{ props.row.title }}</p>
          </b-table-column>

          <b-table-column field="recipes" label="Recipes" centered v-slot="props">
            <p class="has-text-weight-bold has-text-centered">{{ rowNumberRecipes(props.row) }}</p>
          </b-table-column>

          <b-table-column field="position" label="Position" sortable centered v-slot="props">
            <p class="has-text-weight-bold has-text-centered">{{ props.row.position }}</p>
          </b-table-column>

          <b-table-column field="status" width=100 label="Status" sortable centered v-slot="props">
            <FeedsStatusBadge :status="props.row.status" />
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="created_at" width=100 label="Created" sortable centered v-slot="props">
            <span class="date-tag">{{ new Date(props.row.created_at).toLocaleDateString() }}</span>
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="id" width=60 label="ID" v-slot="props">
            <p class="is-size-7 has-text-grey">{{ props.row.id }}</p>
          </b-table-column>

          <b-table-column field="options" custom-key="actions" width=20 v-slot="props">
            <b-dropdown aria-role="list" position="is-bottom-left" @click.native.stop>
              <template #trigger>
                <b-button icon-right="dots-vertical" />
              </template>
              <b-dropdown-item aria-role="listitem" @click="duplicateRecipeCollection(props.row)">
                <b-icon size="is-small" icon="content-copy"></b-icon>
                Duplicate
              </b-dropdown-item>
              <b-dropdown-item class="has-text-danger" aria-role="listitem" @click="deleteRecipeCollection(props.row)">
                <b-icon size="is-small" icon="delete"></b-icon>
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </b-table-column>
        </b-table>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import FeedsStatusBadge from '@/components/common/FeedsStatusBadge.vue'
import TopBar from '@/components/layout/TopBar.vue'

const SCROLL_STATE_KEY = 'feeds_list_state_RecipeCollectionListView'
const SORT_STATE_KEY = 'feeds_list_sort_RecipeCollectionListView'
const FILTER_STATE_KEY = 'feeds_list_filter_RecipeCollectionListView'

export default {
  name: 'RecipeCollectionListView',
  components: {
    FeedsStatusBadge,
    TopBar
  },
  data() {
    const savedSort = JSON.parse(sessionStorage.getItem(SORT_STATE_KEY) || 'null')
    return {
      recipeCollections: [],
      sortField: savedSort?.field || 'created_at',
      sortDirection: savedSort?.direction || 'desc',
      statusFilter: sessionStorage.getItem(FILTER_STATE_KEY) || '',
      scrollRestored: false
    }
  },
  watch: {
    recipeCollections() {
      if (!this.scrollRestored) {
        this.scrollRestored = true
        this.$nextTick(() => {
          const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollY > 0) {
              setTimeout(() => window.scrollTo(state.scrollX, state.scrollY), 50)
            }
          }
        })
      }
    }
  },
  firestore: {
    recipeCollections: db.collection('recipe_collections').orderBy("created_at", "desc")
  },
  computed: {
    collections(){
      return this.recipeCollections.filter(collection => collection.type == "admin-created")
    },
    filteredCollections() {
      if (!this.statusFilter) return this.collections
      return this.collections.filter(c => c.status === this.statusFilter)
    }
  },
  methods: {
    isDesktop() {
      return screen.width > 760
    },
    onSort(field, direction) {
      this.sortField = field
      this.sortDirection = direction
      sessionStorage.setItem(SORT_STATE_KEY, JSON.stringify({ field, direction }))
    },
    onStatusFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY, value)
    },
    createNewRecipeCollection() {
      db.collection("recipe_collections").add({
        created_at: Date.now(),
        title: "New recipe collection",
        description: "A new collection of recipes",
        recipes: {},
        images: [],
        status: "draft",
        type: "admin-created",
        position: 1,
        analytics_identifier: null,
      })
      .then((docRef) => {
        this.$router.push({ name: 'RecipeCollectionView', params: { id: docRef.id } })
      })
      .catch((error) => {
        console.error("Error adding document: ", error);
      });
    },
    openRecipeCollection(recipeCollection) {
      this.$router.push({ name: 'RecipeCollectionView', params: { id: recipeCollection.id } })
    },
    deleteRecipeCollection(recipeCollection) {
      this.$buefy.dialog.confirm({
        title: 'Deleting recipe collection',
        message: `Are you sure you want to delete Recipe Collection: <b><i>"${recipeCollection.title}"</i></b>? <br /><br />This action cannot be undone.`,
        confirmText: 'Delete Recipe Collection',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          db.collection("recipe_collections").doc(recipeCollection.id)
          .delete()
          .then(() => {
            this.$buefy.toast.open({type: 'is-success', message: `Recipe collection successfully deleted!`})
          }).catch((error) => {
            this.$buefy.toast.open({type: 'is-danger', message: `Error deleting recipe collection: ${error}`})
          });
        }
      })
    },
    duplicateRecipeCollection(recipeCollection) {
      db.collection("recipe_collections").doc(recipeCollection.id)
        .get()
        .then((doc) => {
          if (doc.exists && doc.data()) {
            let newData = doc.data();
            newData['created_at'] = Date.now();
            newData['status'] = "draft";
            return db.collection("recipe_collections").add(newData)
              .then((docRef) => {
                this.$buefy.toast.open({type: 'is-success', message: `Recipe Collection successfully duplicated! ${docRef.id}`})
              });
          }
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error duplicating recipe collection ${recipeCollection.id}: ${error}`})
        });
    },
    rowNumberRecipes(recipeCollection){
      return Object.keys(recipeCollection.recipes).length
    },
    rowImageSrc(recipeCollection) {
      if (recipeCollection.images == null) return null;
      return recipeCollection.images.length != 0 ? recipeCollection.images[0].src : null;
    }
  }
}
</script>

<style scoped>
.thumbnail {
  height: 100% !important;
  object-fit: cover;
  border-radius: var(--radius-sm);
}

.date-tag {
  font-size: 12px;
  color: var(--text-secondary);
}
</style>
